import InfoOutlined from "@mui/icons-material/InfoOutlined";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useHcpLandscapeApi, useHcpLandscapeDropdowns, useHcpLandscapeExport } from "../../api/useHcpLandscapeApi";
import { DataWindow, useDataWindows, useMetaData } from "../../api/useMetaDataApi";
import { usePayerLandscapeDetailDropdowns } from "../../api/usePayerLandscapeDetailApi";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import GeoSelect from "../../components/GeoSelect/GeoSelect";
import PageHeader from "../../components/Headers/PageHeader";
import MinimalSelect from "../../components/Inputs/MinimalSelect";
import { RangeType } from "../../components/Inputs/RangeInput";
import Select from "../../components/Inputs/Select";
import { useModal } from "../../components/Modal/useModal";
import SearchBox from "../../components/SearchBox/SearchBox";
import Table from "../../components/Table/Table";
import Tooltip from "../../components/Tooltip/Tooltip";
import {
  useClientHCPLandscapeConfiguration,
  useClientIndicationFiltersconfiguration,
} from "../../data/global/useProductConfiguration";
import { useHcpLandscapeDispatch, useHcpLandscapeState } from "../../data/hcpLandscape/hooks";
import { resolveLandscapeSort } from "../../data/hcpLandscape/reducer";
import useGetRole, { FRM_ROLE } from "../../hooks/useGetRole";
import useHasFootnote from "../../hooks/useHasFootnote";
import { arrayToOptions } from "../../utils/arrayToOptions";
import DetailFiltersModal from "./HCPLandscapeFiltersModal";
import { useHcpLandscapeColumnVisibility } from "./useHCPLandscapeColumnVisibility";
import useTableColumns from "./useTableColumns";

const HCPLandscape: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setModal } = useModal();
  const { client: selectedClient } = useParams() as { client: string };
  const useDefault = !i18n.exists(`hcpLandscape.hcps.${selectedClient}`);
  const { columns } = useTableColumns(useDefault ? "default" : selectedClient);
  const columnNames = columns.map((column) => t(column.label, column.translationParams));
  const columnKeys = columns.map((column) => column.dataKey);
  const [search, setSearch] = useState<string>("");
  const { updateSort, updateFilters } = useHcpLandscapeDispatch();
  const { filters, advancedFilterCount, sort } = useHcpLandscapeState();
  const hcpQuery = useHcpLandscapeApi(selectedClient);
  const exportTable = useHcpLandscapeExport(selectedClient, columnKeys, columnNames);
  const dropdownQuery = usePayerLandscapeDetailDropdowns(selectedClient);
  const dropdowns = dropdownQuery.data;
  const beneOptions = arrayToOptions(dropdowns?.benificiaryType, false, t("common.allChannels"));
  const hcpDropdownQuery = useHcpLandscapeDropdowns(selectedClient);
  const hcpDropdowns = hcpDropdownQuery.data;
  const targetListOptions = arrayToOptions(hcpDropdowns?.targetList, true, t("common.allTargets"));
  const { data: metaData } = useMetaData(selectedClient, filters.timePeriod || "");
  const { data: dataWindows } = useDataWindows(selectedClient);
  const { data: role } = useGetRole(selectedClient);
  const hasHcpLandscapeFootnote = useHasFootnote(selectedClient, "hcpLandscape");
  const {
    indicationFiltering: showIndicationFiltering,
    sortColumn,
    sortDirection,
    targetListFiltering,
  } = useClientHCPLandscapeConfiguration(selectedClient);
  const indicationOptions = useClientIndicationFiltersconfiguration(selectedClient);
  const isFrm = String(role) === FRM_ROLE;
  const { isColumnVisible } = useHcpLandscapeColumnVisibility(selectedClient, filters.indication, isFrm, String(role));
  const isMktTrxVisible = isColumnVisible("mkt_trx");

  const timePeriodOptions = (dataWindows || []).map((dataWindow) => {
    return {
      label: t(`payerLandscape.timePeriodOptions.${dataWindow}`),
      value: dataWindow,
    };
  });
  const defaultDataWindow =
    (dataWindows || []).find((dataWindow) => dataWindow === DataWindow.twelveMonths) || DataWindow.twelveMonths;
  const showTimePeriodSelection = (dataWindows || []).length > 1;

  const openFilterModal = () => {
    setModal(<DetailFiltersModal client={selectedClient} />);
  };

  const previousIndicationRef = useRef<string | null | undefined>(filters.indication);

  //IF INDICATION SET, BUT SHOULDNT BE, SET TO UNDEFINED
  useEffect(() => {
    if (!showIndicationFiltering) {
      updateFilters({ indication: undefined });
    }
  }, [updateFilters, indicationOptions, filters.indication, showIndicationFiltering]);

  // IF TARGET LIST FILTERING TURNED OFF OR VALUE IS INVALID, CLEAR IT
  useEffect(() => {
    if (!targetListFiltering) {
      updateFilters({ segment: undefined });
    } else if (hcpDropdowns?.targetList && filters.segment && !hcpDropdowns.targetList.includes(filters.segment)) {
      updateFilters({ segment: undefined });
    }
  }, [targetListFiltering, filters.segment, hcpDropdowns?.targetList, updateFilters]);

  //IF BENETYPE IS INVALID, SET TO FIRST VALID OPTION
  useEffect(() => {
    if (!dropdowns?.benificiaryType) {
      return;
    }
    if (!dropdowns?.benificiaryType?.includes(filters.benificiaryType || "") && dropdowns?.benificiaryType.length > 0) {
      updateFilters({
        benificiaryType: dropdowns?.benificiaryType[0],
      });
    }
  }, [updateFilters, beneOptions, filters.benificiaryType, dropdowns?.benificiaryType]);

  //IF NO INDICATION SET, SET TO FIRST PRODUCT AVAILABLE
  useEffect(() => {
    if (
      indicationOptions &&
      showIndicationFiltering &&
      indicationOptions !== undefined &&
      indicationOptions.length > 0 &&
      filters.indication === undefined
    ) {
      updateFilters({ indication: indicationOptions[0].value });
    }
  }, [updateFilters, indicationOptions, filters.indication, showIndicationFiltering]);

  //Set the default beneficiaryType to first option (Commerical)
  useEffect(() => {
    if (!filters.benificiaryType && beneOptions.length > 1) {
      updateFilters({ benificiaryType: beneOptions[0].value });
    }
  }, [beneOptions, filters.benificiaryType, updateFilters]);

  //Set the default timePeriod to last 12 months
  useEffect(() => {
    if (!filters.timePeriod && (dataWindows || []).length > 0) {
      updateFilters({ timePeriod: defaultDataWindow });
    }
  }, [dataWindows, defaultDataWindow, filters.timePeriod, updateFilters]);

  //Debounce search and update genericFilter
  useEffect(() => {
    const timeoutAmount = search === "" ? 0 : 400;
    const timeoutId = setTimeout(() => {
      updateFilters({ genericFilter: search });
    }, timeoutAmount);

    //If a new keystroke happens before timeout, clear the timeout
    return () => {
      clearTimeout(timeoutId);
    };
  }, [search, updateFilters]);

  useEffect(() => {
    // Apply the product's sort override, or the visibility-aware default. Resetting on
    // client/config change stops a prior product's sort lingering, and avoids defaulting
    // to mkt_trx when that column is hidden (e.g. FRM views).
    updateSort(resolveLandscapeSort(sortColumn, sortDirection, isMktTrxVisible));
  }, [selectedClient, sortColumn, sortDirection, isMktTrxVisible, updateSort]);

  // Clear filters when indication changes (since available columns/filters change)
  useEffect(() => {
    const currentIndication = filters.indication;
    const previousIndication = previousIndicationRef.current;

    // Only clear filters if indication actually changed (not on initial load)
    if (previousIndication !== undefined && currentIndication !== previousIndication) {
      // Clear HCP filters that might not be relevant to the new indication
      updateFilters({
        indication: currentIndication,
        // Keep core filters that are always visible
        benificiaryType: filters.benificiaryType,
        timePeriod: filters.timePeriod,
        genericFilter: filters.genericFilter, // Keep search
        name: filters.name, // Keep name filter
        // Reset all range filters with proper defaults
        favorableAccessPercentMin: undefined,
        favorableAccessPercentMax: undefined,
        favorableAccessPercentRangeType: RangeType.GE,
        favorableAccessChangePercentMin: undefined,
        favorableAccessChangePercentMax: undefined,
        favorableAccessChangePercentRangeType: RangeType.GE,
        overallAccessPercentMin: undefined,
        overallAccessPercentMax: undefined,
        overallAccessPercentRangeType: RangeType.GE,
        marketTrxMin: undefined,
        marketTrxMax: undefined,
        marketRangeType: RangeType.GE,
        trxMin: undefined,
        trxMax: undefined,
        trxRangeType: RangeType.GE,
        compTrxMin: undefined,
        compTrxMax: undefined,
        compTrxRangeType: RangeType.GE,
        productShareMin: undefined,
        productShareMax: undefined,
        productShareRangeType: RangeType.GE,
      });
    }

    // Update the ref for next comparison
    previousIndicationRef.current = currentIndication;
  }, [
    filters.indication,
    filters.benificiaryType,
    filters.genericFilter,
    filters.name,
    filters.timePeriod,
    updateFilters,
  ]);

  const footnoteContent = useMemo(() => {
    const baseKey = `hcpLandscape.footnote.${selectedClient}`;
    const defaultKey = `hcpLandscape.footnote.default`;

    if (role) {
      const roleSpecificKey = `${baseKey}_${role}`;

      if (i18n.exists(roleSpecificKey)) {
        return t(roleSpecificKey);
      }
    }

    return t([baseKey, defaultKey]);
  }, [t, selectedClient, role, i18n]);

  return (
    <>
      <PageHeader
        title={
          i18n.exists(`common.hcpLandscape.${selectedClient}`)
            ? t(`common.hcpLandscape.${selectedClient}`)
            : t(`common.hcpLandscape.default`)
        }
      >
        <SearchBox value={search} handleChange={setSearch} />
      </PageHeader>

      <BreadCrumbs />

      <div className="pb-4">
        <div className="md:flex md:space-x-10 justify-end">
          <div className={indicationOptions && showIndicationFiltering ? "lg:w-2/12" : ""}>
            {indicationOptions && showIndicationFiltering && (
              <MinimalSelect
                value={filters.indication}
                handleChange={(value) => updateFilters({ indication: value })}
                label={
                  i18n.exists(`payerLandscape.planInfoFor.${selectedClient}`)
                    ? t(`payerLandscape.planInfoFor.${selectedClient}`)
                    : t("payerLandscape.planInfoFor.default")
                }
                options={indicationOptions}
              />
            )}
          </div>
          <div className="w-full xl:w-10/12 md:flex md:space-x-10">
            {targetListFiltering && (
              <Select
                value={filters.segment}
                handleChange={(value) => updateFilters({ segment: value })}
                label={t("hcpLandscape.targetList")}
                options={targetListOptions}
              />
            )}
            {showTimePeriodSelection ? (
              <Select
                value={filters.timePeriod}
                label={t("payerLandscape.timePeriod")}
                options={timePeriodOptions}
                handleChange={(value) => updateFilters({ timePeriod: value })}
              />
            ) : null}
            <GeoSelect client={selectedClient} />
            <Select
              value={filters.benificiaryType}
              handleChange={(value) => updateFilters({ benificiaryType: value })}
              label={
                i18n.exists(`payerLandscape.beneType.${selectedClient}`)
                  ? t(`payerLandscape.beneType.${selectedClient}`)
                  : t("payerLandscape.beneType.default")
              }
              options={beneOptions}
            />
          </div>
        </div>
        <div className="flex md:justify-end">
          <button
            onClick={openFilterModal}
            className="text-primary w-full md:w-auto font-bold py-3 px-2 md:py-1 md:ml-0 md:-mr-2 rounded"
          >
            {t("common.advancedFilterWithCount", { count: advancedFilterCount })}
          </button>
        </div>
      </div>

      <div className="flex flex-row-reverse mb-2">
        {i18n.exists(`hcpLandscape.tooltip.${filters.timePeriod}.${selectedClient}`) && (
          <Tooltip
            placement="bottom-end"
            tip={t([
              `hcpLandscape.tooltip.${filters.timePeriod}.${selectedClient}`,
              `hcpLandscape.tooltip.${filters.timePeriod}.default`,
            ])}
          >
            <InfoOutlined className="text-primary" />
          </Tooltip>
        )}
      </div>

      <Table
        columns={columns}
        query={hcpQuery}
        sort={sort}
        handleSort={(sort) => updateSort(sort)}
        export={exportTable}
        stickyMargin={54}
        metaData={metaData}
        role={String(role)}
        footnote={hasHcpLandscapeFootnote ? footnoteContent : false}
      />
    </>
  );
};

export default HCPLandscape;
